import { gql } from '@apollo/client';
import store from 'store';
import cloneDeep from 'lodash/cloneDeep';
import cloneDeepWith from 'lodash/cloneDeepWith';
import { FIXTURE_FRAGMENT } from '../../fragments/fixtureFragment';
import {
    updateFreightPortBasisIfRequired,
    updateCargoIfRequired,
    updateChannel,
    cleanRatesIfRequired,
    clearDeliveryTermIfRequried,
    updateWeek,
} from '../../../services/QueryPostProcessingService';
import {
    hasFirstZoneChanged,
    getAvailableZone,
    getAvailableArea,
    getAvailableAreaGeared,
    getEastWest,
} from '../../../models/Location';
import {
    CARGO_QTY_FRAGMENT_NAME,
    CARGO_QTY_FRAGMENT,
    CARGO_QTY_TYPE_NAME,
} from '../../fragments/cargoQuantityPartFragment';
import {
    COMPANY_FRAGMENT,
    COMPANY_FRAGMENT_NAME,
    COMPANY_TYPE_NAME,
} from '../../fragments/companyFragment';
import {
    VESSEL_FRAGMENT,
    VESSEL_FRAGMENT_NAME,
    VESSEL_TYPE_NAME,
} from '../../fragments/vesselFragment';
import {
    LAYCAN_FRAGMENT,
    LAYCAN_FRAGMENT_NAME,
    LAYCAN_TYPE_NAME,
} from '../../fragments/laycanFragment';
import {
    LOCATION_PART_FRAGMENT_NAME,
    LOCATION_PART_FRAGMENT,
    LOCATION_PART_TYPE_NAME,
} from '../../fragments/locationPartFragment';
import {
    CARGO_PART_FRAGMENT_NAME,
    CARGO_PART_FRAGMENT,
    CARGO_PART_TYPE_NAME,
} from '../../fragments/cargoPartFragment';
import {
    FREIGHT_PORT_BASIS_TYPE_NAME,
    FREIGHT_PORT_BASIS_FRAGMENT_NAME,
    FREIGHT_PORT_BASIS_FRAGMENT,
} from '../../fragments/freightPortBasisFragment';
import {
    THIRD_PARTY_COMPANY_PART_FRAGMENT,
    THIRD_PARTY_COMPANY_PART_FRAGMENT_NAME,
    THIRD_PARTY_COMPANY_PART_TYPE_NAME,
} from '../../fragments/thirdPartyCompanyPartFragment';
import {
    CLARKSONS_USER_FRAGMENT,
    CLARKSONS_USER_FRAGMENT_NAME,
    CLARKSONS_USER_TYPE_NAME,
} from '../../fragments/clarksonsUserFragment';
import {
    DURATION_PART_FRAGMENT_NAME,
    DURATION_PART_FRAGMENT,
    DURATION_PART_TYPE_NAME,
} from '../../fragments/durationPartFragment';
import { getVesselPostProcessingService } from '_legacy/modules/columns/vessel';
import {
    COMMISSION_FRAGMENT,
    COMMISSION_FRAGMENT_NAME,
    COMMISSION_TYPE_NAME,
} from '_legacy/api/fragments/commissionFragment';
import {
    RATES_FRAGMENT,
    RATES_FRAGMENT_NAME,
    RATES_TYPE_NAME,
} from '_legacy/api/fragments/ratesFragment';
import moment from 'moment';
import { ENTITY_PART_TYPE_COUNTRY } from '../../../models/common/EntityPart';
import LocationApi from '_legacy/api/locations/locationApi';
import CountryApi from '_legacy/api/countryApi';
import {
    TCE_PART_FRAGMENT,
    TCE_PART_FRAGMENT_NAME,
    TCE_PART_TYPE_NAME,
} from '_legacy/api/fragments/tcePartFragment';
import { getVesselCategory } from '_legacy/utils/vesselUtils';
import { CargoApi } from '_legacy/api/cargoApi';

export const FIXTURE_RECENT_CHANGES_FOR_DATASET_QUERY = gql`
    query FixtureChanges($groupId: ID!, $numberOfYears: Int!) {
        publicFixturesRecentlyChanged(
            groupId: $groupId
            numberOfYears: $numberOfYears
        ) {
            ...FixtureFragment
        }

        groupOnlyFixtures(groupId: $groupId, numberOfYears: $numberOfYears) {
            ...FixtureFragment
        }

        individualFixtures(groupId: $groupId, numberOfYears: $numberOfYears) {
            ...FixtureFragment
        }

        fixturesWithPrivateComments(
            groupId: $groupId
            numberOfYears: $numberOfYears
        ) {
            ...FixtureFragment
        }

        unavailableFixtureIdsFromLastSnapshot(
            groupId: $groupId
            numberOfYears: $numberOfYears
        )
    }

    ${FIXTURE_FRAGMENT}
`;

export const VESSEL_FIXTURES_QUERY = gql`
    query VesselFixtures(
        $groupId: ID!
        $vesselImo: Int!
        $numberOfYears: Int!
    ) {
        vesselFixtures(
            groupId: $groupId
            vesselImo: $vesselImo
            numberOfYears: $numberOfYears
        ) {
            ...FixtureFragment
        }
    }

    ${FIXTURE_FRAGMENT}
`;

export const INNER_FIXTURE_MUTATION = `
createFixture(groupId: $groupId, groupOnly: $groupOnly, groupOnlyType: $groupOnlyType, privacy: $privacy, isIndividual: $isIndividual, type: $type, vesselImo: $vesselImo, vesselCategory: $vesselCategory, owner: $owner) {
  ...FixtureFragment
}
`;

export const CREATE_FIXTURE_MUTATION = gql`
  mutation CreateFixture($groupId: ID!, $groupOnly: Boolean!, $groupOnlyType: GroupOnlyType, $privacy: Boolean!, $isIndividual: Boolean!, $type: String!, $vesselImo: Int, $vesselCategory: String, $owner: ThirdPartyCompanyPartInputType) {
    ${INNER_FIXTURE_MUTATION}
  }
  
  ${FIXTURE_FRAGMENT}
`;

export const SHARE_FIXTURE_MUTATION = gql`
    mutation ShareFixture($id: ID!) {
        shareFixture(id: $id) {
            id
            lastUpdatedDateTimeByUser
            lastUpdatedByUser {
                username
                fullName
            }
        }
    }
`;

export const SHARE_FIXTURE_TO_GROUP_MUTATION = gql`
    mutation ShareFixtureToGroup($id: ID!) {
        shareFixtureToGroup(id: $id) {
            id
            lastUpdatedDateTimeByUser
            lastUpdatedByUser {
                username
                fullName
            }
        }
    }
`;

export const DELETE_FIXTURE_MUTATION = gql`
    mutation DeleteFixture($groupId: ID!, $id: ID!) {
        deleteFixture(groupId: $groupId, id: $id) {
            id
            state
            lastUpdatedDateTimeByUser
            lastUpdatedByUser {
                username
                fullName
            }
        }
    }
`;

export const MAKE_FIXTURE_PUBLIC_MUTATION = gql`
    mutation MakeFixturePublic($groupId: ID!, $id: ID!) {
        makeFixturePublic(groupId: $groupId, id: $id) {
            id
            privacy
            privateFields
            lastUpdatedDateTimeByUser
            lastUpdatedByUser {
                username
                fullName
            }
        }
    }
`;

export const MAKE_FIXTURE_PRIVATE_MUTATION = gql`
    mutation MakeFixturePrivate($groupId: ID!, $id: ID!) {
        makeFixturePrivate(groupId: $groupId, id: $id) {
            id
            privacy
            privateFields
            lastUpdatedDateTimeByUser
            lastUpdatedByUser {
                username
                fullName
            }
        }
    }
`;

export const UPDATE_FIXTURE_PRIVATE_FIELDS_MUTATION = gql`
    mutation UpdateFixturePrivateFields(
        $groupId: ID!
        $id: ID!
        $fields: [String]!
    ) {
        updateFixturePrivateFields(
            groupId: $groupId
            id: $id
            fields: $fields
        ) {
            id
            privacy
            privateFields
            lastUpdatedDateTimeByUser
            lastUpdatedByUser {
                username
                fullName
            }
        }
    }
`;

export const MAKE_FIXTURE_UNRUMOURED_MUTATION = gql`
    mutation MakeFixtureUnRumoured($groupId: ID!, $id: ID!) {
        makeFixtureUnRumoured(groupId: $groupId, id: $id) {
            id
            rumoured
            rumouredFields
            lastUpdatedDateTimeByUser
            lastUpdatedByUser {
                username
                fullName
            }
        }
    }
`;

export const MAKE_FIXTURE_RUMOURED_MUTATION = gql`
    mutation MakeFixtureRumoured($groupId: ID!, $id: ID!) {
        makeFixtureRumoured(groupId: $groupId, id: $id) {
            id
            rumoured
            rumouredFields
            lastUpdatedDateTimeByUser
            lastUpdatedByUser {
                username
                fullName
            }
        }
    }
`;

export const UPDATE_FIXTURE_RUMOURED_FIELDS_MUTATION = gql`
    mutation UpdateFixtureRumouredFields(
        $groupId: ID!
        $id: ID!
        $fields: [String]!
    ) {
        updateFixtureRumouredFields(
            groupId: $groupId
            id: $id
            fields: $fields
        ) {
            id
            rumoured
            rumouredFields
            lastUpdatedDateTimeByUser
            lastUpdatedByUser {
                username
                fullName
            }
        }
    }
`;

export const REINSTATE_FIXTURE_MUTATION = gql`
    mutation ReinstateFixture($groupId: ID!, $id: ID!) {
        reinstateFixture(groupId: $groupId, id: $id) {
            id
            state
            lastUpdatedDateTimeByUser
            lastUpdatedByUser {
                username
                fullName
            }
        }
    }
`;

export const CLONE_FIXTURE_WITH_POS_INFO_MUTATION = gql`
    mutation CloneFixtureWithPosInfo($id: ID!, $groupId: ID!) {
        cloneFixtureWithPosInfo(id: $id, groupId: $groupId) {
            ...FixtureFragment
        }
    }

    ${FIXTURE_FRAGMENT}
`;

export const CLONE_FIXTURE_WITH_CARGO_INFO_MUTATION = gql`
    mutation CloneFixtureWithCargoInfo($id: ID!, $groupId: ID!) {
        cloneFixtureWithCargoInfo(id: $id, groupId: $groupId) {
            ...FixtureFragment
        }
    }

    ${FIXTURE_FRAGMENT}
`;

export const GET_MAINTAINANCE_MESSAGE_QUERY = gql`
    query GetAnalyticsMaintainanceMessage($reportName: String!) {
        getAnalyticsMaintainanceMessage(reportName: $reportName)
    }
`;

const addTypeName = (obj, type) => {
    if (Array.isArray(obj)) {
        for (var i = 0; i < obj.length; i++) {
            addTypeName(obj[i], type);
        }
    } else {
        obj['__typename'] = type;
    }
};

const omitDeep = (collection, excludeKeys) => {
    function omitFn(value) {
        if (value && typeof value === 'object') {
            excludeKeys.forEach((key) => {
                delete value[key];
            });
        }
    }
    return cloneDeepWith(collection, omitFn);
};

export const createFixtureFieldMutation = (
    data,
    id,
    field,
    value,
    username,
    userId,
    groupId
) => {
    let mutationStructure = field;
    let fragment = '';
    let fragmentName = '';
    let type = '';
    let variableName = field;
    let customUpdate = null;
    let filtersToBeRefreshed = null;
    let sanitizeVariable = null;
    let mutationValue = cloneDeep(value);
    let gridValue = cloneDeep(value);

    const gridType = 'fixture';

    let variables = {
        groupId: groupId,
        fixture: {
            id: id,
        },
    };

    //TODO: use valueParser column definition instead
    switch (field) {
        case 'laycan':
            mutationStructure = `${field} { ...${LAYCAN_FRAGMENT_NAME} } minRedelivery maxRedelivery`;
            fragment = LAYCAN_FRAGMENT;
            variableName = 'laycanShorthand';
            type = LAYCAN_TYPE_NAME;
            if (mutationValue) {
                mutationValue = mutationValue.shorthand;
            }
            break;

        case 'charterer':
        case 'owner':
            fragmentName = COMPANY_FRAGMENT_NAME;
            fragment = COMPANY_FRAGMENT;
            type = COMPANY_TYPE_NAME;
            break;

        case 'brokers':
            fragmentName = COMPANY_FRAGMENT_NAME;
            fragment = COMPANY_FRAGMENT;
            type = COMPANY_TYPE_NAME;
            filtersToBeRefreshed = ['channel'];

            customUpdate = (data, oldValue, newValue) => {
                updateChannel(data, variables, newValue, gridType);
            };

            break;

        case 'cargoQuantityParts':
            fragmentName = CARGO_QTY_FRAGMENT_NAME;
            fragment = CARGO_QTY_FRAGMENT;
            type = CARGO_QTY_TYPE_NAME;
            break;

        case 'freightPortBasis':
            fragmentName = FREIGHT_PORT_BASIS_FRAGMENT_NAME;
            fragment = FREIGHT_PORT_BASIS_FRAGMENT;
            type = FREIGHT_PORT_BASIS_TYPE_NAME;

            sanitizeVariable = (mutationValue) => {
                let freightPortBasis = {};

                freightPortBasis.shouldBeUpdatedAutomatically =
                    mutationValue.shouldBeUpdatedAutomatically;
                freightPortBasis.parts = mutationValue.parts.map((x) => {
                    return {
                        loadCount: +x.loadCount,
                        dischargeCount: +x.dischargeCount,
                    };
                });

                return freightPortBasis;
            };
            break;

        case 'cargoParts':
            fragmentName = CARGO_PART_FRAGMENT_NAME;
            fragment = CARGO_PART_FRAGMENT;
            type = CARGO_PART_TYPE_NAME;
            filtersToBeRefreshed = ['cleanDirtyCargo'];

            customUpdate = async (data, oldValue, newValue) => {
                const cleanDirtyValue = await getFirstCargoCleanDirtyValue(
                    newValue
                );
                data['cleanDirtyCargo'] = cleanDirtyValue;
                variables.fixture['cleanDirtyCargo'] = cleanDirtyValue;
            };
            break;

        case 'commission':
            fragmentName = COMMISSION_FRAGMENT_NAME;
            fragment = COMMISSION_FRAGMENT;
            type = COMMISSION_TYPE_NAME;
            break;

        case 'thirdPartyCharterer':
        case 'thirdPartyOwner':
        case 'buyer':
        case 'operator':
            fragmentName = THIRD_PARTY_COMPANY_PART_FRAGMENT_NAME;
            fragment = THIRD_PARTY_COMPANY_PART_FRAGMENT;
            type = THIRD_PARTY_COMPANY_PART_TYPE_NAME;
            break;

        case 'thirdPartyBrokers':
            fragmentName = THIRD_PARTY_COMPANY_PART_FRAGMENT_NAME;
            fragment = THIRD_PARTY_COMPANY_PART_FRAGMENT;
            type = THIRD_PARTY_COMPANY_PART_TYPE_NAME;
            filtersToBeRefreshed = ['channel'];

            customUpdate = (data, oldValue, newValue) => {
                updateChannel(data, variables, newValue, gridType);
            };

            break;

        case 'clarksonsBrokers':
            fragmentName = CLARKSONS_USER_FRAGMENT_NAME;
            fragment = CLARKSONS_USER_FRAGMENT;
            type = CLARKSONS_USER_TYPE_NAME;
            break;

        case 'rates':
            fragmentName = RATES_FRAGMENT_NAME;
            fragment = RATES_FRAGMENT;
            type = RATES_TYPE_NAME;
            break;

        case 'vessel.design':
        case 'vessel.yard':
        case 'vessel.dwt':
        case 'vessel.tankTypeCode':
        case 'vessel.gasCbm':
        case 'vessel.yob':
        case 'vessel.scrubber':
        case 'vessel.iceClassNotation':
        case 'vessel.imoClass':
        case 'vessel.gasType':
        case 'vessel.engineType':
        case 'vessel.propType':
        case 'vessel.alternativeFuelType':
            fragmentName = VESSEL_FRAGMENT_NAME;
            fragment = VESSEL_FRAGMENT;
            type = VESSEL_TYPE_NAME;

            const fields = field.split('.');
            let vessel = {};
            vessel[fields[1]] = mutationValue;

            field = fields[0];
            mutationStructure = fields[0];
            variableName = fields[0];
            mutationValue = vessel;
            gridValue = vessel;
            break;

        case 'vessel':
            fragmentName = VESSEL_FRAGMENT_NAME;
            fragment = VESSEL_FRAGMENT;
            type = VESSEL_TYPE_NAME;
            filtersToBeRefreshed = ['vesselCategory'];

            customUpdate = (data, oldValue, newValue) => {
                let vesselCategory = getVesselCategory(
                    newValue,
                    data.datasetId
                );

                data['vesselCategory'] = vesselCategory;
                variables.fixture['vesselCategory'] = vesselCategory;

                const vesselService = getVesselPostProcessingService(
                    data.datasetId
                );

                if (vesselService) {
                    vesselService.updateOwnerIfRequired(
                        newValue,
                        data,
                        variables,
                        'fixture'
                    );
                }
            };

            sanitizeVariable = (mutationValue) => {
                delete mutationValue['bestOperator'];
                delete mutationValue['gainCompany'];
                delete mutationValue['shouldUpdateOwner'];
                delete mutationValue['fleetTypeIds'];
                return mutationValue;
            };
            break;
        case 'salePrice':
        case 'awrp':
            if (mutationValue) {
                mutationValue = parseFloat(mutationValue);
            }
            break;
        case 'cpDate':
            if (data['status'] === 'C') {
                updateWeek(mutationValue, data, variables, gridType);
            }
            break;
        case 'status':
            if (mutationValue) {
                const cpDate =
                    mutationValue === 'C' ? moment().startOf('day') : null;
                data['cpDate'] = cpDate;
                variables[gridType]['cpDate'] = cpDate;
                updateWeek(cpDate, data, variables, gridType);
            }
            break;
        case 'loadEW':
        case 'dischEW':
            setIntraInterRegional(
                data,
                variables,
                data['loadEW'],
                data['dischEW']
            );
            break;
        case 'loadLocationParts':
            mutationStructure = `${field} { ...${LOCATION_PART_FRAGMENT_NAME} } loadZone loadZones areas`;
            fragment = LOCATION_PART_FRAGMENT;
            type = LOCATION_PART_TYPE_NAME;
            filtersToBeRefreshed = [
                'freightPortBasis',
                'loadZones',
                'loadEW',
                'areas',
                'areasGeared',
                'direction',
                'intraInterRegional',
            ];

            customUpdate = async (data, oldValue, newValue) => {
                updateFreightPortBasisIfRequired(
                    data,
                    variables,
                    mutationValue,
                    'dischargeLocationParts',
                    gridType
                );

                if (hasFirstZoneChanged(data.loadZones?.[0], newValue)) {
                    const zone = getAvailableZone(newValue);
                    data['loadZones'] = zone ? [zone] : [];
                    variables.fixture['loadZones'] = zone ? [zone] : [];
                    setEW(data, variables, newValue, 'loadEW');
                    setIntraInterRegional(
                        data,
                        variables,
                        data['loadEW'],
                        data['dischEW']
                    );
                }

                if (!newValue) {
                    const groupSettings = store
                        .getState()
                        .user.groupsSettings.find(
                            (x) => x.groupId == data.groupId
                        )?.groupSettings;
                    let gearedDeliveryLocations =
                        groupSettings?.useDeliveryZoneForAreaGearedMapping
                            ? await getLocationsIfEmpty(
                                  data.deliveryLocationParts,
                                  null,
                                  data.datasetId
                              )
                            : [];
                    setGearedArea(data, variables, gearedDeliveryLocations);

                    let deliveryLocations =
                        groupSettings?.useDeliveryZoneForAreaGearlessMapping ||
                        groupSettings?.useDeliveryZoneForAreaMapping
                            ? await getLocationsIfEmpty(
                                  data.deliveryLocationParts,
                                  gearedDeliveryLocations,
                                  data.datasetId
                              )
                            : [];
                    setArea(data, variables, deliveryLocations);
                } else {
                    setArea(data, variables, newValue);
                    setGearedArea(data, variables, newValue);
                }
            };

            // Don't send zone and area in the mutation to the graph.
            // They're only present for the purpose of setting area when changing load zone.
            sanitizeVariable = (mutationValue) => {
                if (mutationValue == null) {
                    return mutationValue;
                }

                let values = [];

                for (let i = 0; i < mutationValue.length; i++) {
                    values.push({
                        name: mutationValue[i].name,
                        partType: mutationValue[i].partType,
                        value: mutationValue[i].value,
                        isSTS: mutationValue[i].isSTS,
                    });
                }
                return values;
            };

            break;

        case 'dischargeLocationParts':
            mutationStructure = `${field} { ...${LOCATION_PART_FRAGMENT_NAME} } dischargeZone dischargeZones`;
            fragment = LOCATION_PART_FRAGMENT;
            type = LOCATION_PART_TYPE_NAME;
            filtersToBeRefreshed = [
                'freightPortBasis',
                'dischargeZones',
                'dischEW',
                'direction',
                'intraInterRegional',
            ];

            customUpdate = (data, oldValue, newValue) => {
                updateFreightPortBasisIfRequired(
                    data,
                    variables,
                    mutationValue,
                    'loadLocationParts',
                    gridType
                );

                if (hasFirstZoneChanged(data.dischargeZones?.[0], newValue)) {
                    const zone = getAvailableZone(newValue);
                    data['dischargeZones'] = zone ? [zone] : [];
                    variables.fixture['dischargeZones'] = zone ? [zone] : [];
                    setEW(data, variables, newValue, 'dischEW');
                    setIntraInterRegional(
                        data,
                        variables,
                        data['loadEW'],
                        data['dischEW']
                    );
                }
            };

            // Don't send zone and area in the mutation to the graph.
            // They're only present for the purpose of setting area when changing load zone.
            sanitizeVariable = (mutationValue) => {
                if (mutationValue == null) {
                    return mutationValue;
                }
                let values = [];
                for (let i = 0; i < mutationValue.length; i++) {
                    values.push({
                        name: mutationValue[i].name,
                        partType: mutationValue[i].partType,
                        value: mutationValue[i].value,
                        isSTS: mutationValue[i].isSTS,
                    });
                }
                return values;
            };

            break;

        case 'lastOpenLocationParts':
        case 'actualDischarge':
        case 'actualLoad':
        case 'redeliveryLocationParts':
            fragmentName = LOCATION_PART_FRAGMENT_NAME;
            fragment = LOCATION_PART_FRAGMENT;
            type = LOCATION_PART_TYPE_NAME;
            sanitizeVariable = (mutationValue) => {
                if (mutationValue == null) {
                    return mutationValue;
                }

                let values = [];
                for (let i = 0; i < mutationValue.length; i++) {
                    values.push({
                        name: mutationValue[i].name,
                        partType: mutationValue[i].partType,
                        value: mutationValue[i].value,
                        isSTS: mutationValue[i].isSTS,
                    });
                }
                return values;
            };
            customUpdate = (data, oldValue, newValue) => {
                setEW(data, variables, newValue, 'redeliveryEW');
            };
            break;

        case 'deliveryLocationParts':
            fragmentName = LOCATION_PART_FRAGMENT_NAME;
            fragment = LOCATION_PART_FRAGMENT;
            type = LOCATION_PART_TYPE_NAME;
            filtersToBeRefreshed = [
                'deliveryZones',
                'areasGeared',
                'areas',
                'deliveryEW',
            ];
            customUpdate = (data, oldValue, newValue) => {
                if (hasFirstZoneChanged(data.deliveryZones?.[0], newValue)) {
                    const zone = getAvailableZone(newValue);
                    data['deliveryZones'] = zone ? [zone] : [];
                    variables.fixture['deliveryZones'] = zone ? [zone] : [];
                    setEW(data, variables, newValue, 'deliveryEW');
                }
                const groupSettings = store
                    .getState()
                    .user.groupsSettings.find(
                        (x) => x.groupId == data.groupId
                    )?.groupSettings;
                if ((data.loadLocationParts?.length ?? 0) < 1) {
                    if (groupSettings?.useDeliveryZoneForAreaGearedMapping) {
                        setGearedArea(data, variables, newValue);
                    }
                    if (
                        groupSettings?.useDeliveryZoneForAreaGearlessMapping ||
                        groupSettings?.useDeliveryZoneForAreaMapping
                    ) {
                        setArea(data, variables, newValue);
                    }
                }
            };

            sanitizeVariable = (mutationValue) => {
                if (mutationValue == null) {
                    return mutationValue;
                }

                let values = [];
                for (let i = 0; i < mutationValue.length; i++) {
                    values.push({
                        name: mutationValue[i].name,
                        partType: mutationValue[i].partType,
                        value: mutationValue[i].value,
                        isSTS: mutationValue[i].isSTS,
                    });
                }
                return values;
            };

            break;

        case 'dischargeZone':
        case 'dischargeZones':
            mutationStructure = 'dischargeZones dischargeZone';
            filtersToBeRefreshed = ['direction'];
            break;
        case 'loadZone':
        case 'loadZones':
            mutationStructure = 'loadZones loadZone';
            filtersToBeRefreshed = ['direction'];
            break;

        case 'type':
            cleanRatesIfRequired(data, variables, value, gridType);
            updateCargoIfRequired(data, variables, value, gridType);
            clearDeliveryTermIfRequried(data, variables, value, gridType);
            break;

        case 'durationParts':
            mutationStructure = `${field} { ...${DURATION_PART_FRAGMENT_NAME} } minRedelivery maxRedelivery`;
            fragment = DURATION_PART_FRAGMENT;
            type = DURATION_PART_TYPE_NAME;
            break;

        case 'tceParts':
            fragmentName = TCE_PART_FRAGMENT_NAME;
            fragment = TCE_PART_FRAGMENT;
            type = TCE_PART_TYPE_NAME;
            break;

        default:
            break;
    }

    if (fragmentName) {
        mutationStructure = `${field} { ...${fragmentName} }`;
    }

    if (type && gridValue) {
        addTypeName(gridValue, type);
    }

    const innerMutation = `
  updateFixture(groupId: $groupId, fixture: $fixture) {
    id
    ${mutationStructure}
    lastUpdatedDateTimeByUser
    lastUpdatedByUser {
      username
      fullName
    }
  }`;

    const mutation = gql`
    mutation set_fixture_${field}($groupId: ID!, $fixture: FixtureInput!) {
      ${innerMutation}
    }

    ${fragment}
  `;

    if (mutationValue && sanitizeVariable) {
        variables.fixture[variableName] = sanitizeVariable(mutationValue);
    } else {
        variables.fixture[variableName] = mutationValue;
    }

    //remove any __typename properties that may be in the value else GraphQL will reject the mutation as having
    //an extra field in the input properties
    variables = omitDeep(variables, ['__typename']);

    return {
        variables,
        mutation,
        innerMutation,
        mutationValue,
        gridValue,
        customUpdate,
        filtersToBeRefreshed,
    };
};

const getLocationsIfEmpty = async (locationsPart, locations, datasetId) => {
    if (locations?.length > 0) {
        return locations;
    }

    if (!(locationsPart?.length > 0)) {
        return [];
    }

    const locationPart = locationsPart[0];
    if (locationPart.partType === ENTITY_PART_TYPE_COUNTRY) {
        const countryResponse = await CountryApi.getExtendedCountriesByIds(
            [locationPart.value],
            datasetId
        );
        const country = countryResponse[0];
        country.deskZone = country.zone;
        country.deskArea = country.area;
        country.deskAreaGeared = country.areaGeared;
        return [country];
    } else {
        const locationResponse = await LocationApi.getLocationsByIds(
            [locationPart.value],
            'deliveryLocationParts',
            datasetId,
            false
        );
        return locationResponse;
    }
};

const getFirstCargoCleanDirtyValue = async (selectedCargoes) => {
    if (!selectedCargoes || selectedCargoes.length === 0) {
        return '';
    }

    const [firstCargo] = selectedCargoes;
    return firstCargo.cleanDirty === undefined
        ? await getCleanDirtyForCargo(firstCargo.value)
        : firstCargo.cleanDirty;
};

const getCleanDirtyForCargo = async (id) => {
    const cargo = await CargoApi.getByIds([id]);
    return cargo?.[0]?.cleanDirty ?? '';
};

const setIntraInterRegional = (data, variables, loadEW, dischEW) => {
    let intraInterRegional = null;

    if (!loadEW && !dischEW) {
        intraInterRegional = null;
    } else if (!loadEW || !dischEW) {
        intraInterRegional = 'EXTRA-REGIONAL';
    } else if (loadEW === dischEW) {
        intraInterRegional = 'INTRA-BASIN';
    } else {
        intraInterRegional = 'EXTRA-REGIONAL';
    }

    data['intraInterRegional'] = intraInterRegional;
    variables.fixture['intraInterRegional'] = intraInterRegional;
};

const setEW = (data, variables, location, columnName) => {
    const eastWest = getEastWest(location);
    data[columnName] = eastWest;
    variables.fixture[columnName] = eastWest;
};

const setArea = (data, variables, location) => {
    const area = getAvailableArea(location);
    data['areas'] = area;
    variables.fixture['areas'] = area;
};

const setGearedArea = (data, variables, location) => {
    const areaGeared = getAvailableAreaGeared(location);
    data['areasGeared'] = areaGeared;
    variables.fixture['areasGeared'] = areaGeared;
};
